import { useMemo, useEffect, useState, useCallback, useRef } from 'react'
import {
  Window,
  Button,
  useWindowContext,
  PageRouter,
  useTheme,
  toggleBodyClass,
  setRoute,
  Modal,
  Status,
  noop,
  Icon,
} from '@iqueue/ui-kit'
import LogsPage from './pages/logs/LogsPage'
import ProductionPage from './pages/production/ProductionPage'
import NodesPage from './pages/node/NodesPage'
import useTranslate from './hooks/useTranslate'
import SensorsPage from './pages/sensor/SensorsPage'
import useNav from './hooks/useNav'
import NodePresenter from './api/nodePresenter'
import SensorPresenter from './api/sensorPresenter'
import { isMobile } from 'is-mobile'
import MapView from './pages/map/MapView'
import UserPresenter from './api/userPresenter'
import UsersPage from './pages/user/UsersPage'
import ReportPage from './pages/report/ReportPage'
import ReportPresenter from './api/reportPresenter'
import ResponsibilitiesPresenter from './api/responsibilitiesPresenter'
import ResponsibilitiesPage from './pages/responsibilities/ResponsibilitiesPage'
import Auth from './api/auth'
import RolesPage from './pages/role/RolesPage'
import RolePresenter from './api/rolePresenter'
import VerificationPage from './pages/verification/VerificationPage'
import VerificationPresenter from './api/verificationPresenter'
import NoticePage from './pages/notices/NoticePage'
import NoticesPresents from './api/notciesPresents'
import { NotificationProvider } from './components/NotificationContext'
import ModelPage from './pages/model/ModelPage'
import NoPermissions from './pages/accessControl/NoPermissions'
import InspectionLogsPage from './pages/inspection-logs/InspectionLogsPage'
import InspectionPresenter from './api/InspectionPresenter'
import OptionPage from './pages/option/OptionPage'
import OptionPresenter from './api/optionPresenter'
import UserProfilePage from './pages/user-profile/UserProfilePage'
import AuthForm from './pages/accessControl/Auth'
import usePermissions from './components/usePermissions'
import moment from 'moment'
import SettingsPage from './pages/settings/SettingsPage'
import SettingsPresenter from './api/SettingsPresenter'
import UploadLicense from './pages/uploadLicense/uploadLicense'
import LicensePresenter from './api/licensePrecenter'
import TunerPage from './pages/tuner/TunerPage'
import StatePage from './pages/state/StatePage'

const routesWithoutNav = ['map', 'logs', 'production', 'model']

const locales = {
  ru: 'Русский',
  en: 'English',
  uz: 'Ўзбек тили',
}

/*
 iqBackground: string;
 iqDarken: string;
 iqColor: string;
 iqColorSecondary: string;
 iqDivider: string;
 iqOutline: string;
 iqBackdrop?: string;
 iqScrollbar: string;
 iqScrollbarTrack: string;
 iqScrollbarThumb: string;
 skeletonColor?: string;
 skeletonIconColor?: string;
 iqAccent?: string;
 iqAccentText?: string;
 iqSuccess?: string;
 iqSuccessText?: string;
 iqError?: string;
 iqErrorText?: string;
 iqWarning?: string;
 iqWarningText?: string;
 iqAccentSecondary?: string;
 iqAccentSecondaryText?: string;
 iqButtonColor?: string;
 iqButtonHoverColor?: string;
 iqButtonHoverBackground?: string;
 iqButtonProgress?: string;
 iqButtonProgressActive?: string;
 iqInputBorderColor?: string;
 iqInputBorder?: string;
 iqAccentHover?: string;
 iqAccentHoverText?: string;
 iqSuccessHover?: string;
 iqSuccessHoverText?: string;
 iqErrorHover?: string;
 iqErrorHoverText?: string;
 iqWarningHover?: string;
 iqWarningHoverText?: string;
 iqAccentSecondaryHover?: string;
 iqAccentSecondaryHoverText?: string;
 iqInputBorderRadius?: string;
 iqSVGLogo?: string;
 */

function LicenseExpiredDialog() {
  const [license, setLicense] = useState(null)
  const currentTime = moment()

  const [statusProps, setStatusProps] = useState({
    error: false,
    remainingTime: '',
    overdueDays: 0,
    warning: false,
  })

  const updateStatus = useCallback(() => {
    const expireAt = moment.unix(license?.expiration)

    if (currentTime.isAfter(expireAt)) {
      const overdueDuration = currentTime.diff(expireAt, 'days')
      setStatusProps({
        error: true,
        remainingTime: expireAt.format('L'),
        overdueDays: overdueDuration,
        warning: false,
      })
    }
  }, [currentTime])

  useEffect(() => {
    if (process.env.REACT_APP_ENV === 'development') return

    const listener = (e) => {
      setLicense(e.data)
    }

    document.addEventListener("ab-license", listener)
    return () => {
      document.removeEventListener("ab-license", listener)
    }
  }, [])

  useEffect(() => {
    if (license) {
      updateStatus()
    }
  }, [license, updateStatus])

  return <Modal
    className={ 'license-modal-box' }
    isOpened={ !!license }
    onClose={ () => setLicense(null) }
  >
    <h1>
      <Icon>warning</Icon>
    </h1>
    <h1>Статус лицензии</h1>
    <div className={ currentTime.isAfter(license?.expiration) ? 'license-box' : 'info-license' }>
      <div className="left-column">
        <Status error>
          <b>Дата окончания: { license?.expiration.format('DD.MM.YYYY') }</b>
        </Status>
      </div>
      { currentTime.isAfter(license?.expiration) && <div>
        <div className="separator">
          <b>–</b>
        </div>
        <div className="right-column">
          { statusProps.error && (
            <Status weak inline error={ statusProps.error }>
              Лицензия просрочена на: { statusProps.overdueDays }{ " " }
              { statusProps.overdueDays + 'д' }
            </Status>
          ) }
        </div>
      </div> }

    </div>
  </Modal>
}

function LicenseHeader() {
  const isExpired = useRef(false)
  const [license, setLicense] = useState({
    expiration: null,
  })

  useEffect(() => {
    const listener = (e) => {
      isExpired.current = true
      setLicense(e.data)
    }

    document.addEventListener('ab-license', listener)
    return () => {
      document.removeEventListener('ab-license', listener)
    }
  }, [])

  if (!isExpired.current) return null
  return <div className="license-view">
    Обновите лицензию до: { license.expiration.locale('ru').format('ll') }
  </div>
}

function App() {
  const nav = useNav()
  const { setLocale, locale } = useTranslate()
  const { setTheme } = useTheme()
  const { route, setTitle } = useWindowContext()
  const { auth } = usePermissions()

  const navMode = useMemo(() => {
    return routesWithoutNav.filter((v) => route.startsWith(v)).length ? 'floating' : undefined
  }, [route])

  useEffect(() => {
    toggleBodyClass(isMobile() ? 'iq-touch' : 'iq-pointer', true)
    toggleBodyClass('with-scroll-bars', !isMobile())
  }, [])

  useEffect(() => {
    setTitle('Amber Beard')
  }, [])

  useEffect(() => {
    setTheme('auto')
  }, [setTheme])

  useEffect(() => {
    if (!auth) setTitle('Amber Beard')
    // togglePaddings(false)
  }, [auth])

  useEffect(() => {
    moment.locale(locale === 'uz' ? 'uz/latn' : locale)
  }, [locale])

  return <>
    <LicenseHeader/>
    <Window
      smallScreenWidth={ 640 }
      mediumScreenWidth={ 1024 }
      navMode={ navMode }
      nav={ nav }
      setTitle={ setTitle }
      extra={ <>
        { !!auth && <>
          <Button
            icon={ 'person' }
            flat
            onClick={ () => {
              setRoute('user-integration')
            } }
          />
        </> }
        { <Button
          icon={ 'language' }
          flat
          onClick={ (e) => {
            Modal.List.show({
              items: ['ru', 'en', 'uz'],
              selected: locale,
              target: e.target,
              render: (v) => ({
                id: v,
                title: locales[v],
              }),
            })
              .then(setLocale)
              .catch(noop)
          } }
        /> }
        { !!auth && <Button
          icon={ 'logout' }
          flat
          onClick={ () => {
            Auth.logout()
            setTimeout(() => {
              setRoute('/auth-form')
              window.location.reload()
            }, 100)
          } }
        /> }
      </> }
    >
      <NotificationProvider/>
      <LicenseExpiredDialog/>
      <PageRouter>
        <LogsPage urn={ 'logs' }/>
        {/*<HierarchyPage urn={ 'state' } />*/ }
        <MapView urn={ 'map' }/>
        <NoPermissions urn={ 'no-permissions' }/>
        <AuthForm urn={ 'auth-form' }/>
        <ProductionPage urn={ 'production' }/>
        <ModelPage urn={ 'model' }/>
        <UserProfilePage urn={ 'user-integration' }/>
        <InspectionLogsPage urn={ InspectionPresenter.urn }/>
        {/*<UploadExcelPage urn={ 'upload' }/>*/ }
        {/*<UserProfilePage urn={ UserProfilePresenter.urn }/>*/ }
        <NodesPage urn={ NodePresenter.urn }/>
        <SensorsPage urn={ SensorPresenter.urn }/>
        {/*<FabricatorsPage urn={ FabricatorPresenter.urn }/>*/ }
        <UsersPage urn={ UserPresenter.urn }/>
        {/*<ProfilePage urn={ ProfilePresenter.urn } />*/ }
        <OptionPage urn={ OptionPresenter.urn }/>
        <ResponsibilitiesPage urn={ ResponsibilitiesPresenter.urn }/>
        <NoticePage urn={ NoticesPresents.urn }/>
        <ReportPage urn={ ReportPresenter.urn }/>
        <RolesPage urn={ RolePresenter.urn }/>
        <VerificationPage urn={ VerificationPresenter.urn }/>
        <SettingsPage urn={ SettingsPresenter.urn }/>
        <UploadLicense urn={ LicensePresenter.urn }/>
        <StatePage urn={ 'state' }/>
        <TunerPage urn={ 'tuner' }/>
      </PageRouter>
    </Window>
  </>
}

export default App
